<template>
  <el-dialog
    v-model="visible"
    class="global-biz-opening"
    width="520px"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="preventClose"
    align-center
    append-to-body
  >
    <div v-if="contract" class="opening-body" v-loading="loading">
      <section v-if="step === 'verify-email' || step === 'verify-code'" class="opening-section verification-section">
        <div class="opening-success-head">
          <EnterpriseIcon :src="circleCheckIcon" class="opening-success-head__icon" />
          <h2>{{ $t('enterprise_ui_global_activation_success') }}</h2>
          <p>{{ orderSummary }}</p>
        </div>
        <div class="opening-divider" />

        <div class="opening-section-title">
          <EnterpriseIcon :src="shieldCheckIcon" />
          <h3>{{ $t('enterprise_ui_security_verify_title') }}</h3>
        </div>
        <p class="opening-description">
          {{ $t('enterprise_ui_security_verify_desc', { site: counterpartSiteName }) }}
        </p>

        <div v-for="region in verificationRegions" :key="region" class="verification-field">
          <label>{{ regionEmailLabel(region) }}</label>
          <el-input
            :model-value="emailForRegion(region)"
            readonly
            :placeholder="regionEmailPlaceholder(region)"
          />
          <div class="verification-code-row">
            <el-input
              :model-value="codeForRegion(region)"
              maxlength="6"
              inputmode="numeric"
              :placeholder="$t('enterprise_ui_verification_code_placeholder')"
              @update:model-value="setRegionCode(region, $event)"
            />
            <el-button
              plain
              type="primary"
              :disabled="!isValidEmail(accountEmail)"
              @click="issueVerification"
            >
              {{ regionChallenge(region) ? $t('enterprise_ui_get_code_again') : $t('enterprise_ui_get_code') }}
            </el-button>
          </div>
        </div>

        <el-button class="opening-primary-button" type="primary" :disabled="!canCompleteVerification" @click="completeVerification">
          {{ $t('enterprise_ui_complete_verification') }}
        </el-button>
        <p class="opening-footnote">
          {{ $t('enterprise_ui_verification_footnote', { count: 10, email: 'enterprise@doclingo.ai' }) }}
        </p>
      </section>

      <section v-else-if="step === 'binding' && preflight" class="opening-section binding-section">
        <h2 class="opening-title">{{ $t('enterprise_ui_confirm_dual_region_binding') }}</h2>
        <p class="opening-description">{{ remoteActionDescription }}</p>

        <dl class="preflight-summary">
          <div>
            <dt>{{ $t('enterprise_ui_other_site_email') }}</dt>
            <dd>{{ contract.remoteAccount }}</dd>
          </div>
          <div>
            <dt>{{ $t('enterprise_ui_account_status') }}</dt>
            <dd>{{ preflight.userExists ? $t('enterprise_ui_already_exists') : $t('enterprise_ui_will_auto_create') }}</dd>
          </div>
          <div>
            <dt>{{ $t('enterprise_ui_enterprise') }}</dt>
            <dd>{{ preflight.businessName || (preflight.bizTeamId ? $t('enterprise_ui_enterprise_id', { id: preflight.bizTeamId }) : $t('enterprise_ui_will_auto_create')) }}</dd>
          </div>
        </dl>

        <div v-if="preflight.localEntitlementActive" class="destructive-panel">
          <strong>{{ $t('enterprise_ui_remote_local_plan_void_title') }}</strong>
          <span>{{ $t('enterprise_ui_original_plan_level_value', { name: localServiceLevelName(preflight.localServiceLevel) }) }}</span>
          <span>{{ $t('enterprise_ui_remaining_chars_value', { value: formatCharacters(preflight.localRemainingQuota) }) }}</span>
          <span>{{ $t('enterprise_ui_expiration_time_value', { date: formatDate(preflight.localEndTime) }) }}</span>
          <span>{{ $t('enterprise_ui_no_refund_no_restore') }}</span>
        </div>

        <div v-if="isBlockingRemoteAction" class="blocking-panel">
          {{ remoteActionDescription }}
        </div>

        <el-checkbox
          v-if="requiresConsent"
          v-model="bindingConsent"
          class="irreversible-checkbox"
        >
          {{ $t('enterprise_ui_binding_consent') }}
        </el-checkbox>

        <el-button class="opening-primary-button" type="primary" :disabled="!canPrepareBinding" @click="prepareBinding">
          {{ $t('enterprise_ui_confirm_create_dual_region_binding') }}
        </el-button>
      </section>

      <section
        v-else-if="step === 'allocation'"
        class="opening-section allocation-section"
        :class="{ 'allocation-section--reminder': allocationReminder }"
      >
        <div v-if="allocationReminder" class="allocation-reminder-title-row">
          <h2 class="opening-title">{{ $t('enterprise_ui_complete_quota_allocation_title') }}</h2>
          <button type="button" :aria-label="$t('enterprise_ui_close_allocation_reminder')" @click="postponeAllocation">
            <EnterpriseIcon :src="xIcon" />
          </button>
        </div>
        <h2 v-else class="opening-title">{{ $t('enterprise_ui_allocate_global_quota_title') }}</h2>

        <div v-if="allocationReminder" class="allocation-reminder-warning">
          <EnterpriseIcon :src="triangleAlertIcon" />
          <span>{{ isUpgrade
            ? $t('enterprise_ui_upgrade_allocation_warning')
            : $t('enterprise_ui_global_allocation_warning') }}</span>
        </div>
        <p v-else class="opening-description">
          {{ $t('enterprise_ui_allocation_desc', { quota: formatCharacters(totalQuota) }) }}
          <template v-if="isUpgrade">{{ $t('enterprise_ui_allocation_upgrade_desc') }}</template>
        </p>

        <div class="quota-allocation-bar" :aria-label="$t('enterprise_ui_dual_region_quota_ratio')">
          <span class="quota-allocation-bar__vip" :style="{ flexBasis: `${vipQuotaPercent}%` }" />
          <span class="quota-allocation-bar__ai" :style="{ flexBasis: `${100 - vipQuotaPercent}%` }" />
        </div>

        <div class="quota-presets">
          <button
            v-for="preset in allocationPresets"
            :key="preset.vipPercent"
            type="button"
            :class="{ active: activeAllocationPreset === preset.vipPercent }"
            @click="applyAllocationPreset(preset.vipPercent)"
          >
            {{ preset.label }}
          </button>
          <button type="button" :class="{ active: activeAllocationPreset === null }">{{ $t('enterprise_ui_custom') }}</button>
        </div>

        <div v-if="confirmedSeatCount > seatLimit" class="member-cleanup-panel">
          <div class="member-cleanup-panel__warning">
            <el-icon><WarningFilled /></el-icon>
            <span>{{ $t('enterprise_ui_member_cleanup_warning', { count: confirmedSeatCount, limit: seatLimit }) }}</span>
          </div>
          <div v-for="region in regions" :key="region" class="member-cleanup-region">
            <strong>{{ $t('enterprise_ui_region_members_count', { region: regionShortName(region), count: regionMembers(region).length }) }}</strong>
            <div v-for="member in regionMembers(region)" :key="`${region}-${member.id}`" class="member-cleanup-row">
              <span>{{ member.aliasName || member.userAccount }}</span>
              <el-button v-if="member.roleType !== 1" link type="danger" @click="removeMember(member)">{{ $t('enterprise_ui_remove') }}</el-button>
            </div>
          </div>
        </div>

        <div class="quota-region-grid">
          <div v-for="region in regions" :key="region" class="quota-region-card">
            <strong>{{ allocationRegionName(region) }}</strong>
            <div class="quota-region-card__input">
              <el-input-number
                :model-value="quotaWan(region)"
                :min="0"
                :max="totalQuotaWan"
                :step="0.0001"
                :precision="4"
                :controls="false"
                @change="setQuotaWan(region, $event)"
              />
              <span>{{ $t('enterprise_ui_wan_chars_unit') }}</span>
            </div>
          </div>
        </div>

        <div class="allocation-note">
          <EnterpriseIcon :src="infoIcon" />
          <span>{{ allocationNoteText }}</span>
        </div>

        <el-button
          v-if="allocationReminder"
          class="opening-primary-button allocation-reminder-submit"
          type="primary"
          :disabled="!canAllocate"
          @click="submitAllocation"
        >
          {{ $t('enterprise_ui_confirm_allocation') }}
        </el-button>
        <div v-else class="allocation-actions">
          <el-button class="allocation-secondary-button" @click="postponeAllocation">
            {{ $t('enterprise_ui_allocate_later') }}
          </el-button>
          <el-button class="opening-primary-button" type="primary" :disabled="!canAllocate" @click="submitAllocation">
            {{ $t('enterprise_ui_confirm_allocation') }}
          </el-button>
        </div>
        <p class="opening-footnote allocation-footnote">
          {{ allocationReminder
            ? $t('enterprise_ui_allocation_reminder_footnote')
            : $t('enterprise_ui_allocation_later_footnote') }}
        </p>
      </section>

      <section v-else-if="step === 'waiting'" class="opening-section waiting-section">
        <div v-if="!isPaymentFulfillmentPending" class="waiting-spinner" />
        <h3>{{ waitingTitle }}</h3>
        <p>{{ waitingDescription }}</p>
        <div v-if="contract.status === 'MANUAL_PENDING'" class="manual-pending">
          <strong>{{ $t('enterprise_ui_manual_pending_title') }}</strong>
          <span v-if="isPaymentFulfillmentPending">{{ $t('enterprise_ui_order_no_value', { orderNo: getGlobalBizOpeningOrderNo(contract) }) }}</span>
          <span>{{ $t('enterprise_ui_failure_step_value', { step: contract.failureStep || '-' }) }}</span>
          <span>{{ $t('enterprise_ui_failure_reason_value', { reason: contract.failureReason || $t('enterprise_ui_waiting_admin_check') }) }}</span>
          <span>{{ isPaymentFulfillmentPending
            ? $t('enterprise_ui_payment_manual_pending_desc')
            : $t('enterprise_ui_manual_pending_desc') }}</span>
        </div>
        <p v-if="pollingPaused">{{ $t('enterprise_ui_polling_paused') }}</p>
        <el-button v-if="isPaymentFulfillmentPending || pollingPaused" :loading="checking" @click="recheckOpening">{{ $t('enterprise_ui_recheck') }}</el-button>
      </section>

      <section v-else-if="step === 'success'" class="opening-section success-section">
        <div class="opening-success-head">
          <EnterpriseIcon :src="circleCheckIcon" class="opening-success-head__icon" />
          <h2>{{ $t('enterprise_ui_quota_allocation_complete') }}</h2>
        </div>
        <div v-for="region in regions" :key="region" class="allocation-summary-row">
          <strong>{{ allocationRegionName(region) }}</strong>
          <span>{{ $t('enterprise_ui_annual_quota_active', { quota: formatWanCharacters(region === 'VIP' ? vipQuota : aiQuota) }) }}</span>
        </div>
        <p class="opening-description">
          {{ $t('enterprise_ui_allocation_success_desc') }}
        </p>
        <el-button class="opening-primary-button" type="primary" @click="enterEnterpriseWorkspace">{{ $t('enterprise_ui_enter_workspace') }}</el-button>
      </section>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ElMessageBox } from 'element-plus'
import { WarningFilled } from '@element-plus/icons-vue'
import { storeToRefs } from 'pinia'
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import circleCheckIcon from '~/assets/images/enterprise/icons/circle-check.svg'
import infoIcon from '~/assets/images/enterprise/icons/info.svg'
import shieldCheckIcon from '~/assets/images/enterprise/icons/shield-check.svg'
import triangleAlertIcon from '~/assets/images/enterprise/icons/triangle-alert.svg'
import xIcon from '~/assets/images/enterprise/icons/x.svg'
import GlobalBizServer from '~/services/GlobalBizServer'
import type {
  GlobalBizContract,
  GlobalBizMember,
  GlobalBizRemotePreflight,
  GlobalBizSite,
  GlobalBizVerificationChallenge,
} from '~/services/GlobalBizServer/types'
import useUserStore from '~/store/modules/user'
import { useEnterpriseBalanceAlert } from '~/composables/useEnterpriseBalanceAlert'
import Toast from '~/utils/toast'
import {
  forgetGlobalBizAllocationPrompt,
  forgetGlobalBizOrder,
  getDefaultGlobalBizAllocation,
  getGlobalBizAccountEmail,
  getGlobalBizOpeningKey,
  getGlobalBizOpeningOrderNo,
  getRememberedGlobalBizOrder,
  globalBizRightsKey,
  GLOBAL_BIZ_ORDER_CREATED_EVENT,
  hasSeenGlobalBizAllocationPrompt,
  isGlobalBizOpeningStatus,
  isGlobalBizUpgrade,
  isValidGlobalBizAllocation,
  markGlobalBizAllocationPromptSeen,
  requiresIrreversibleConsent,
} from '~/utils/globalBizOpening'

type OpeningStep = 'verify-email' | 'verify-code' | 'binding' | 'allocation' | 'waiting' | 'success'
type ChallengeState = GlobalBizVerificationChallenge & {
  code: string
  verified: boolean
  submitting: boolean
}

const NESTED_CONFIRM_MODAL_CLASS = 'global-biz-nested-confirm-overlay'

const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()
const enterpriseBalanceAlert = useEnterpriseBalanceAlert()
const { token, userInfo, bizTeamInfo, roleRightsMap, isUserInfoReady } = storeToRefs(userStore)

const visible = ref(false)
const loading = ref(false)
const checking = ref(false)
const contract = ref<GlobalBizContract | null>(null)
const step = ref<OpeningStep>('verify-email')
const challenges = ref<ChallengeState[]>([])
const preflight = ref<GlobalBizRemotePreflight | null>(null)
const preflightContractNo = ref('')
const bindingConsent = ref(false)
const members = ref<GlobalBizMember[]>([])
const aiQuota = ref(0)
const vipQuota = ref(0)
const allocationBasis = ref('')
const attemptedPreflightContract = ref('')
const allocationPostponed = ref(false)
const allocationPromptKey = ref('')
const allocationReminder = ref(false)
const completedOpeningKey = ref('')
let queuedOrderNo = ''
let pollTimer: ReturnType<typeof setInterval> | null = null

const regions: GlobalBizSite[] = ['VIP', 'AI']
const counterpartSite = computed<GlobalBizSite>(() => contract.value?.ownerSite === 'VIP' ? 'AI' : 'VIP')
const verificationRegions = computed<GlobalBizSite[]>(() => contract.value ? [counterpartSite.value] : [])
const counterpartSiteName = computed(() => counterpartSite.value === 'VIP'
  ? t('enterprise_ui_china_version_domain')
  : t('enterprise_ui_international_version_domain'))
const isEmailLoginAccount = computed(() => Number(userInfo.value?.accountType) === 0)
const allocationPresets = [
  { vipPercent: 50, label: '50 / 50' },
  { vipPercent: 70, label: '70 / 30' },
  { vipPercent: 30, label: '30 / 70' },
]

const accountEmail = computed(() => getGlobalBizAccountEmail(userInfo.value?.email, contract.value?.buyerAccount))
const isUpgrade = computed(() => isGlobalBizUpgrade(contract.value))
const isPaymentFulfillmentPending = computed(() => contract.value?.status === 'MANUAL_PENDING'
  && contract.value.failureStep === 'PAYMENT_FULFILLMENT')
const totalQuota = computed(() => effectiveTotalQuota(contract.value))
const seatLimit = computed(() => effectiveSeatLimit(contract.value))
const confirmedSeatCount = computed(() => members.value.filter(member => member.status === 1).length)
const requiresConsent = computed(() => requiresIrreversibleConsent(preflight.value?.action))
const isBlockingRemoteAction = computed(() => [
  'ACTIVE_GLOBAL_CONTRACT_CONFLICT',
  'CHANGE_EMAIL_REQUIRED',
  'REMOTE_STATE_CONFLICT',
].includes(preflight.value?.action || ''))
const canPrepareBinding = computed(() => {
  if (!contract.value?.contractNo || !preflight.value || isBlockingRemoteAction.value) return false
  return !requiresConsent.value || bindingConsent.value
})
const canAllocate = computed(() => {
  return Boolean(contract.value?.contractNo)
    && contract.value?.status === 'ALLOCATION_PENDING'
    && confirmedSeatCount.value <= seatLimit.value
    && isValidGlobalBizAllocation(totalQuota.value, aiQuota.value, vipQuota.value)
})

const planName = computed(() => {
  const names: Record<string, string> = {
    BASE: t('enterprise_ui_global_base_plan'),
    PROFESSIONAL: t('enterprise_ui_global_professional_plan'),
    ADVANCED: t('enterprise_ui_global_advanced_plan'),
  }
  return names[contract.value?.planCode || ''] || t('enterprise_ui_global_enterprise_plan')
})

const paymentSnapshot = computed<Record<string, any>>(() => {
  try {
    return JSON.parse(contract.value?.planSnapshotJson || '{}')
  } catch {
    return {}
  }
})

const orderSummary = computed(() => {
  const openingOrderNo = getGlobalBizOpeningOrderNo(contract.value)
  const orderNo = openingOrderNo
    ? t('enterprise_ui_order_no_short', { orderNo: openingOrderNo })
    : t('enterprise_ui_global_enterprise_order')
  const amount = Number(paymentSnapshot.value.payAmount)
  const coinType = String(paymentSnapshot.value.coinType || '').toUpperCase()
  if (!Number.isFinite(amount) || !coinType) return `${orderNo} · ${planName.value}`
  const currencySymbols: Record<string, string> = {
    CNY: '¥', USD: '$', EUR: '€', GBP: '£', JPY: '¥', KRW: '₩', HKD: 'HK$', TWD: 'NT$',
  }
  return `${orderNo} · ${planName.value} ${currencySymbols[coinType] || `${coinType} `}${amount.toLocaleString()}${t('enterprise_ui_per_year_suffix')}`
})

const totalQuotaWan = computed(() => Number((totalQuota.value / 10000).toFixed(4)))
const vipQuotaPercent = computed(() => totalQuota.value > 0
  ? Math.min(100, Math.max(0, (vipQuota.value / totalQuota.value) * 100))
  : 50)
const activeAllocationPreset = computed(() => {
  const rounded = Math.round(vipQuotaPercent.value)
  return allocationPresets.some(item => item.vipPercent === rounded) ? rounded : null
})
const canCompleteVerification = computed(() => {
  if (challenges.value.length !== 1) return false
  return challenges.value.every(challenge => challenge.verified || /^\d{6}$/.test(challenge.code))
})

const waitingTitle = computed(() => {
  if (isPaymentFulfillmentPending.value) return t('enterprise_ui_waiting_title_payment_manual')
  if (contract.value?.status === 'REFUNDING') return t('enterprise_ui_waiting_title_refunding')
  if (contract.value?.status === 'MANUAL_PENDING') return t('enterprise_ui_waiting_title_manual')
  return t('enterprise_ui_waiting_title_processing')
})

const waitingDescription = computed(() => {
  if (isPaymentFulfillmentPending.value) {
    if (contract.value?.failureReason === 'SOURCE_PLAN_CHANGED') {
      return t('enterprise_ui_waiting_desc_source_plan_changed')
    }
    if (contract.value?.failureReason === 'GLOBAL_PLAN_CONFLICT') {
      return t('enterprise_ui_waiting_desc_global_plan_conflict')
    }
    return t('enterprise_ui_waiting_desc_payment_manual')
  }
  if (contract.value?.status === 'REFUNDING') return t('enterprise_ui_waiting_desc_refunding')
  if (contract.value?.status === 'ACCOUNT_BINDING_PENDING') return t('enterprise_ui_waiting_desc_binding')
  if (contract.value?.status === 'PROVISIONING') return t('enterprise_ui_waiting_desc_provisioning')
  if (contract.value?.status === 'PROVISION_FAILED') return t('enterprise_ui_waiting_desc_provision_failed')
  return t('enterprise_ui_waiting_desc_processing')
})

const remoteActionDescription = computed(() => {
  const action = preflight.value?.action
  const descriptions: Record<string, string> = {
    CREATE_ACCOUNT_AND_ENTERPRISE: t('enterprise_ui_remote_action_create_account_and_enterprise'),
    CREATE_ENTERPRISE: t('enterprise_ui_remote_action_create_enterprise'),
    LEAVE_AND_CREATE_ENTERPRISE: t('enterprise_ui_remote_action_leave_and_create_enterprise'),
    BIND_EXISTING_ENTERPRISE: t('enterprise_ui_remote_action_bind_existing_enterprise'),
    BIND_EXISTING_ENTERPRISE_WITH_OVERRIDE: t('enterprise_ui_remote_action_bind_existing_enterprise_override'),
    LOCAL_ENTITLEMENT_CONFLICT: t('enterprise_ui_remote_action_local_entitlement_conflict'),
    ACTIVE_GLOBAL_CONTRACT_CONFLICT: t('enterprise_ui_remote_action_active_global_conflict'),
    CHANGE_EMAIL_REQUIRED: t('enterprise_ui_remote_action_change_email_required'),
    REMOTE_STATE_CONFLICT: t('enterprise_ui_remote_action_remote_state_conflict'),
  }
  return descriptions[action || ''] || t('enterprise_ui_remote_action_default')
})

const allocationNoteText = computed(() => t('enterprise_ui_allocation_note', {
  extra: isUpgrade.value
    ? t('enterprise_ui_allocation_note_upgrade')
    : t('enterprise_ui_allocation_note_new'),
}))

function preventClose() {
  // 全球套餐开通完成前禁止关闭，保持当前页面处于阻塞状态。
}

async function checkOpeningContract(preferredOrderNo = '') {
  if (!token.value || !isUserInfoReady.value) return
  if (checking.value) {
    if (preferredOrderNo) queuedOrderNo = preferredOrderNo
    return
  }
  const requestedToken = token.value
  checking.value = true
  try {
    const pendingResponse = await GlobalBizServer.pendingContract()
    if (token.value !== requestedToken) return
    if (pendingResponse.code === '1' && pendingResponse.data) {
      applyContract(pendingResponse.data)
      return
    }

    const orderNo = preferredOrderNo || getRememberedGlobalBizOrder() || getGlobalBizOpeningOrderNo(contract.value)
    if (!orderNo) return
    const contractResponse = await GlobalBizServer.contract(orderNo)
    if (token.value !== requestedToken) return
    if (contractResponse.code !== '1' || !contractResponse.data) return
    const latest = contractResponse.data
    // 新订单尚未生成待分配视图时，不能把原合同的 ACTIVE 当成本次升级成功。
    if (getGlobalBizOpeningOrderNo(latest) !== orderNo) return
    applyContract(latest)
  } catch {
    // 支付完成和合同创建存在短暂时差，轮询会继续恢复流程。
  } finally {
    checking.value = false
    if (queuedOrderNo) {
      const nextOrderNo = queuedOrderNo
      queuedOrderNo = ''
      void checkOpeningContract(nextOrderNo)
    }
  }
}

function applyContract(nextContract: GlobalBizContract) {
  const previousContractNo = contract.value?.contractNo
  const previousStatus = contract.value?.status
  const openingChanged = getGlobalBizOpeningKey(contract.value) !== getGlobalBizOpeningKey(nextContract)
  if (openingChanged || previousStatus !== nextContract.status) {
    automaticPollAttempts = 0
    pollingPaused.value = false
  }

  if (openingChanged) {
    challenges.value = []
    preflight.value = null
    preflightContractNo.value = ''
    bindingConsent.value = false
    attemptedPreflightContract.value = ''
    allocationPostponed.value = false
    allocationPromptKey.value = ''
    allocationReminder.value = false
    allocationBasis.value = ''
    aiQuota.value = 0
    vipQuota.value = 0
    members.value = []
  }

  contract.value = {
    ...nextContract,
    remoteAccount: getGlobalBizAccountEmail(userInfo.value?.email, nextContract.buyerAccount),
  }
  if (['REFUNDED', 'EXPIRED'].includes(nextContract.status)) {
    visible.value = false
    forgetGlobalBizAllocationPrompt(getGlobalBizOpeningKey(nextContract))
    if (getRememberedGlobalBizOrder() === getGlobalBizOpeningOrderNo(nextContract)) forgetGlobalBizOrder()
    if (previousStatus !== nextContract.status || openingChanged) {
      Toast(nextContract.status === 'REFUNDED'
        ? t('enterprise_ui_toast_global_order_refunded')
        : t('enterprise_ui_toast_global_order_ended'))
    }
    return
  }
  visible.value = true

  if (nextContract.status === 'ACTIVE') {
    completeOpening(nextContract)
    return
  }
  if (nextContract.status === 'ALLOCATION_PENDING' && nextContract.contractNo) {
    configureAllocationPrompt(getGlobalBizOpeningKey(nextContract))
    step.value = 'allocation'
    visible.value = !allocationPostponed.value
    if (previousStatus !== nextContract.status || openingChanged) {
      void loadMembersAndRights()
    }
    return
  }
  if (!nextContract.contractNo
    || ['ACCOUNT_BINDING_PENDING', 'PROVISIONING', 'PROVISION_FAILED', 'MANUAL_PENDING', 'REFUNDING'].includes(nextContract.status)) {
    step.value = 'waiting'
    return
  }
  if (nextContract.status === 'ACCOUNT_BINDING_CONFLICT') {
    preflight.value = null
    preflightContractNo.value = ''
    bindingConsent.value = false
    step.value = 'verify-email'
    return
  }
  if (nextContract.status === 'VERIFY_PENDING') {
    if (preflight.value && preflightContractNo.value === nextContract.contractNo) {
      step.value = 'binding'
      return
    }
    if (!challenges.value.length || previousContractNo !== nextContract.contractNo) {
      // 邮箱密码账号由后端复用当前登录身份，预检期间不闪现验证码表单。
      step.value = isEmailLoginAccount.value ? 'waiting' : 'verify-email'
    }
    if (contract.value.remoteAccount && attemptedPreflightContract.value !== nextContract.contractNo) {
      attemptedPreflightContract.value = nextContract.contractNo
      void runPreflight(true)
    }
  }
}

async function issueVerification() {
  if (!contract.value?.contractNo || !isValidEmail(accountEmail.value)) return
  loading.value = true
  try {
    const response = await GlobalBizServer.issueVerification({
      contractNo: contract.value.contractNo,
      remoteEmail: accountEmail.value,
    })
    if (response.code !== '1' || !response.data) {
      Toast(response.message || t('enterprise_ui_toast_code_send_failed'))
      return
    }
    challenges.value = response.data.challenges
      .filter(challenge => challenge.region === counterpartSite.value)
      .map(challenge => ({
        ...challenge,
        code: '',
        verified: false,
        submitting: false,
      }))
    contract.value.remoteAccount = accountEmail.value
    if (!challenges.value.length) {
      step.value = 'waiting'
      await runPreflight(false)
      return
    }
    step.value = 'verify-code'
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_toast_code_send_failed'))
  } finally {
    loading.value = false
  }
}

async function confirmChallenge(challenge: ChallengeState) {
  if (challenge.verified) return true
  if (!/^\d{6}$/.test(challenge.code)) return false
  challenge.submitting = true
  try {
    const response = await GlobalBizServer.confirmVerification({
      challengeId: challenge.challengeId,
      code: challenge.code,
    })
    if (response.code !== '1' || !response.data) {
      Toast(response.message || t('enterprise_ui_toast_code_incorrect'))
      return false
    }
    challenge.verified = true
    return true
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_toast_code_verify_failed'))
    return false
  } finally {
    challenge.submitting = false
  }
}

async function completeVerification() {
  if (!canCompleteVerification.value || loading.value) return
  loading.value = true
  try {
    for (const challenge of challenges.value) {
      const verified = await confirmChallenge(challenge)
      if (!verified) return
    }
    if (challenges.value.every(item => item.verified)) {
      await runPreflight(false)
    }
  } finally {
    loading.value = false
  }
}

async function runPreflight(silent: boolean) {
  if (!contract.value?.contractNo || !contract.value.remoteAccount) return
  const requestedContractNo = contract.value.contractNo
  const requestedOpeningKey = getGlobalBizOpeningKey(contract.value)
  const requestedEmail = contract.value.remoteAccount
  loading.value = !silent
  try {
    const response = await GlobalBizServer.remotePreflight({
      contractNo: requestedContractNo,
      email: requestedEmail,
      conflictResolution: 'NONE',
    })
    if (response.code !== '1' || !response.data) {
      if (!silent) Toast(response.message || t('enterprise_ui_toast_remote_preflight_failed'))
      return
    }
    if (getGlobalBizOpeningKey(contract.value) !== requestedOpeningKey) return
    preflight.value = response.data
    preflightContractNo.value = requestedContractNo
    bindingConsent.value = false
    if (isEmailLoginAccount.value
      && response.data.action === 'CREATE_ACCOUNT_AND_ENTERPRISE') {
      step.value = 'waiting'
      await prepareBinding()
      return
    }
    step.value = 'binding'
  } catch (error: any) {
    if (!silent) Toast(error?.message || t('enterprise_ui_toast_remote_preflight_failed'))
  } finally {
    loading.value = false
  }
}

async function prepareBinding() {
  if (!contract.value?.contractNo || !preflight.value || !canPrepareBinding.value) return
  loading.value = true
  try {
    const override = ['LOCAL_ENTITLEMENT_CONFLICT', 'BIND_EXISTING_ENTERPRISE_WITH_OVERRIDE']
      .includes(preflight.value.action)
    const response = await GlobalBizServer.prepareBinding({
      contractNo: contract.value.contractNo,
      businessName: bizTeamInfo.value?.businessName,
      conflictResolution: override ? 'OVERRIDE' : 'NONE',
      consentToken: preflight.value.overrideConsentToken,
      entitlementSnapshotHash: preflight.value.entitlementSnapshotHash,
    })
    if (response.code !== '1') {
      Toast(response.message || t('enterprise_ui_toast_dual_region_binding_failed'))
      await runPreflight(false)
      return
    }
    step.value = 'waiting'
    contract.value.status = 'ACCOUNT_BINDING_PENDING'
    await checkOpeningContract(getGlobalBizOpeningOrderNo(contract.value))
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_toast_dual_region_binding_failed'))
  } finally {
    loading.value = false
  }
}

async function loadMembersAndRights() {
  if (!contract.value?.contractNo || contract.value.status !== 'ALLOCATION_PENDING') return
  const requestedOpeningKey = getGlobalBizOpeningKey(contract.value)
  const requestedContractNo = contract.value.contractNo
  loading.value = true
  try {
    await userStore.getRoleRights()
    // 首次开通在正式分配前合同额度为 0，需先使用套餐权益初始化界面。
    initializeAllocation()
    const response = await GlobalBizServer.members(requestedContractNo)
    if (getGlobalBizOpeningKey(contract.value) !== requestedOpeningKey) return
    if (response.code !== '1' || !response.data) {
      // 兼容先发布 Web、Java 尚未放开待分配成员查询的短暂窗口；最终席位仍由分配接口校验。
      if (response.code === '407' && contract.value?.status === 'ALLOCATION_PENDING') return
      Toast(response.message || t('enterprise_ui_toast_dual_region_members_failed'))
      return
    }
    members.value = response.data.members || []
    initializeAllocation()
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_toast_dual_region_members_failed'))
  } finally {
    loading.value = false
  }
}

function initializeAllocation(force = false) {
  if (!contract.value || totalQuota.value <= 0) return
  const basis = `${getGlobalBizOpeningKey(contract.value)}:${totalQuota.value}`
  if (!force && allocationBasis.value === basis) return
  allocationBasis.value = basis
  const initialAllocation = getDefaultGlobalBizAllocation(totalQuota.value)
  aiQuota.value = initialAllocation.aiQuota
  vipQuota.value = initialAllocation.vipQuota
}

function setQuota(region: GlobalBizSite, value: number) {
  if (region === 'AI') {
    aiQuota.value = clampQuota(value)
    vipQuota.value = totalQuota.value - aiQuota.value
  } else {
    vipQuota.value = clampQuota(value)
    aiQuota.value = totalQuota.value - vipQuota.value
  }
}

function applyAllocationPreset(vipPercent: number) {
  setQuota('VIP', Math.round(totalQuota.value * vipPercent / 100))
}

function quotaWan(region: GlobalBizSite) {
  return Number(((region === 'VIP' ? vipQuota.value : aiQuota.value) / 10000).toFixed(4))
}

function setQuotaWan(region: GlobalBizSite, value: number | undefined) {
  setQuota(region, Math.round(Number(value || 0) * 10000))
}

function clampQuota(value: number) {
  const integer = Math.round(Number(value || 0))
  return Math.min(totalQuota.value, Math.max(0, integer))
}

async function removeMember(member: GlobalBizMember) {
  if (!contract.value?.contractNo || member.roleType === 1) return
  try {
    await ElMessageBox.confirm(
      t('enterprise_ui_remove_member_confirm_desc', { account: member.userAccount, site: siteName(member.region) }),
      t('enterprise_ui_remove_member_confirm_title'),
      {
        type: 'warning',
        confirmButtonText: t('enterprise_ui_confirm_remove'),
        cancelButtonText: t('enterprise_ui_cancel'),
        modalClass: NESTED_CONFIRM_MODAL_CLASS,
      },
    )
  } catch {
    return
  }

  loading.value = true
  try {
    const response = await GlobalBizServer.removeMember({
      contractNo: contract.value.contractNo,
      region: member.region,
      memberId: member.id,
    })
    if (response.code !== '1') {
      Toast(response.message || t('enterprise_ui_toast_remove_member_failed'))
      return
    }
    members.value = members.value.filter(item => !(item.region === member.region && item.id === member.id))
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_toast_remove_member_failed'))
  } finally {
    loading.value = false
  }
}

async function submitAllocation() {
  if (!contract.value?.contractNo || !canAllocate.value || loading.value) return
  const requestedOpeningKey = getGlobalBizOpeningKey(contract.value)
  const orderNo = getGlobalBizOpeningOrderNo(contract.value)
  const configuredTotalBeforeRefresh = totalQuota.value
  loading.value = true
  try {
    const currentResponse = await GlobalBizServer.contract(orderNo)
    if (getGlobalBizOpeningKey(contract.value) !== requestedOpeningKey) return
    if (currentResponse.code !== '1' || !currentResponse.data) {
      Toast(currentResponse.message || t('enterprise_ui_toast_contract_query_failed'))
      return
    }
    const current = currentResponse.data
    if (getGlobalBizOpeningOrderNo(current) !== orderNo) {
      Toast(t('enterprise_ui_toast_upgrade_order_not_synced'))
      return
    }
    if (getGlobalBizOpeningKey(current) !== requestedOpeningKey || current.status !== 'ALLOCATION_PENDING') {
      applyContract(current)
      Toast(t('enterprise_ui_toast_contract_status_updated'))
      return
    }
    if (effectiveTotalQuota(current) !== configuredTotalBeforeRefresh
      || current.currentAllocationVersion !== contract.value.currentAllocationVersion) {
      applyContract(current)
      initializeAllocation(true)
      Toast(t('enterprise_ui_toast_allocation_config_updated'))
      return
    }
    const response = await GlobalBizServer.allocate({
      contractNo: contract.value.contractNo,
      orderNo,
      aiQuota: aiQuota.value,
      vipQuota: vipQuota.value,
      expectedAllocationVersion: Number(contract.value.currentAllocationVersion || 0),
    })
    if (getGlobalBizOpeningKey(contract.value) !== requestedOpeningKey) return
    if (response.code !== '1' || !response.data) {
      if (response.code === '10064') {
        Toast(t('enterprise_ui_toast_allocation_status_updated'))
        await checkOpeningContract(orderNo)
      } else {
        Toast(response.message || t('enterprise_ui_toast_dual_region_allocation_failed'))
      }
      await loadMembersAndRights()
      return
    }
    contract.value.currentAllocationVersion = response.data.allocationVersion
    contract.value.currentCycleNo = response.data.cycleNo
    contract.value.totalQuota = response.data.totalQuota
    aiQuota.value = Number(response.data.aiQuota || 0)
    vipQuota.value = Number(response.data.vipQuota || 0)
    contract.value.aiQuota = aiQuota.value
    contract.value.vipQuota = vipQuota.value
    if (response.data.status === 'ACTIVE') {
      completeOpening({ ...contract.value, status: 'ACTIVE' })
    } else {
      contract.value.status = response.data.status
      step.value = 'waiting'
    }
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_toast_dual_region_allocation_failed'))
  } finally {
    loading.value = false
  }
}

function postponeAllocation() {
  allocationPostponed.value = true
  visible.value = false
}

function configureAllocationPrompt(openingKey: string) {
  if (allocationPromptKey.value === openingKey) return
  allocationPromptKey.value = openingKey
  allocationReminder.value = hasSeenGlobalBizAllocationPrompt(openingKey)
  if (!allocationReminder.value) markGlobalBizAllocationPromptSeen(openingKey)
}

function completeOpening(activeContract: GlobalBizContract) {
  const openingKey = getGlobalBizOpeningKey(activeContract)
  if (completedOpeningKey.value === openingKey) return
  completedOpeningKey.value = openingKey
  contract.value = activeContract
  aiQuota.value = Number(activeContract.aiQuota ?? aiQuota.value)
  vipQuota.value = Number(activeContract.vipQuota ?? vipQuota.value)
  visible.value = true
  step.value = 'success'
  forgetGlobalBizAllocationPrompt(openingKey)
  forgetGlobalBizOrder()
  void refreshOpeningEntitlements()
}

async function refreshOpeningEntitlements() {
  await Promise.allSettled([userStore.getUserInfo(), userStore.getBizTeamInfo()])
  await enterpriseBalanceAlert.refresh(true)
}

async function enterEnterpriseWorkspace() {
  visible.value = false
  await router.push({ path: '/account', query: { menu: 'enterprise' } })
}

function regionMembers(region: GlobalBizSite) {
  return members.value.filter(member => member.region === region)
}

function regionShortName(region: GlobalBizSite) {
  return region === 'VIP'
    ? t('enterprise_ui_domestic_site_dot_vip')
    : t('enterprise_ui_overseas_site_dot_ai')
}

function allocationRegionName(region: GlobalBizSite) {
  return region === 'VIP'
    ? t('enterprise_ui_domestic_doclingo_vip')
    : t('enterprise_ui_overseas_doclingo_ai')
}

function effectiveTotalQuota(target?: GlobalBizContract | null) {
  const stored = Number(target?.totalQuota || 0)
  if (stored > 0) return stored
  const key = globalBizRightsKey(target?.planCode)
  return Number(key ? roleRightsMap.value?.[key]?.charsMaxLimit || 0 : 0)
}

function effectiveSeatLimit(target?: GlobalBizContract | null) {
  const stored = Number(target?.seatLimit || 0)
  if (stored > 0) return stored
  const key = globalBizRightsKey(target?.planCode)
  return Number(key ? roleRightsMap.value?.[key]?.memberCountLimit || 0 : 0)
}

function regionEmailLabel(region: GlobalBizSite) {
  return region === 'VIP'
    ? t('enterprise_ui_domestic_enterprise_email')
    : t('enterprise_ui_overseas_enterprise_email')
}

function regionEmailPlaceholder(_region: GlobalBizSite) {
  return t('enterprise_ui_current_user_email_readonly')
}

function emailForRegion(_region: GlobalBizSite) {
  return accountEmail.value
}

function regionChallenge(region: GlobalBizSite) {
  return challenges.value.find(challenge => challenge.region === region)
}

function codeForRegion(region: GlobalBizSite) {
  return regionChallenge(region)?.code || ''
}

function setRegionCode(region: GlobalBizSite, value: string) {
  const challenge = regionChallenge(region)
  if (challenge) challenge.code = value.replace(/\D/g, '').slice(0, 6)
}

function siteName(site?: GlobalBizSite) {
  return site === 'VIP'
    ? t('enterprise_ui_domestic_site_vip')
    : t('enterprise_ui_global_site_ai')
}

function localServiceLevelName(level?: number) {
  return ({
    1: t('enterprise_ui_plan_base'),
    2: t('enterprise_ui_plan_professional'),
    3: t('enterprise_ui_plan_advanced'),
  } as Record<number, string>)[level || 0]
    || t('enterprise_ui_level_value', { level: level || '-' })
}

function formatCharacters(value?: number) {
  return t('enterprise_ui_chars_value', { count: Number(value || 0).toLocaleString() })
}

function formatWanCharacters(value?: number) {
  const count = (Number(value || 0) / 10000).toLocaleString('zh-CN', {
    maximumFractionDigits: 4,
  })
  return t('enterprise_ui_wan_chars_value', { count })
}

function formatDate(value?: string) {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function handleOrderCreated(event: Event) {
  automaticPollAttempts = 0
  pollingPaused.value = false
  const orderNo = (event as CustomEvent<{ orderNo?: string }>).detail?.orderNo || ''
  void checkOpeningContract(orderNo)
}

watch([token, isUserInfoReady], ([currentToken, ready]) => {
  if (!currentToken) {
    visible.value = false
    contract.value = null
    allocationPostponed.value = false
    allocationPromptKey.value = ''
    allocationReminder.value = false
    completedOpeningKey.value = ''
    allocationBasis.value = ''
    challenges.value = []
    preflight.value = null
    preflightContractNo.value = ''
    attemptedPreflightContract.value = ''
    queuedOrderNo = ''
    return
  }
  if (ready) void checkOpeningContract(getRememberedGlobalBizOrder())
}, { immediate: true })

const pollingPaused = ref(false)
let automaticPollAttempts = 0

function recheckOpening() {
  automaticPollAttempts = 0
  pollingPaused.value = false
  void checkOpeningContract()
}

function pollOpening() {
  if (checking.value || isPaymentFulfillmentPending.value || pollingPaused.value) return
  if (typeof document !== 'undefined' && document.hidden) return
  if ((visible.value && isGlobalBizOpeningStatus(contract.value?.status))
    || (!allocationPostponed.value && getRememberedGlobalBizOrder())) {
    if (automaticPollAttempts >= 100) {
      pollingPaused.value = true
      return
    }
    automaticPollAttempts += 1
    void checkOpeningContract()
  }
}

onMounted(() => {
  window.addEventListener(GLOBAL_BIZ_ORDER_CREATED_EVENT, handleOrderCreated)
  if (!pollTimer) pollTimer = setInterval(pollOpening, 3000)
})

onBeforeUnmount(() => {
  window.removeEventListener(GLOBAL_BIZ_ORDER_CREATED_EVENT, handleOrderCreated)
  if (pollTimer) clearInterval(pollTimer)
})
</script>

<style lang="scss">
.el-overlay.global-biz-nested-confirm-overlay {
  z-index: 10050 !important;
}

.el-dialog.global-biz-opening {
  width: min(760px, calc(100vw - 32px)) !important;
  max-width: calc(100vw - 32px);
  padding: 0 !important;
  border-radius: 14px;

  .el-dialog__header {
    height: auto !important;
    margin: 0 !important;
    padding: 24px 28px 18px !important;
    border-bottom: 1px solid #edf0f5 !important;
  }

  .el-dialog__body {
    padding: 22px 28px 28px !important;
  }
}

.opening-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  h2 {
    margin: 4px 0 0;
    color: #1f2937;
    font-size: 22px;
  }

  &__eyebrow {
    color: #3a7afe;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .08em;
  }

  &__status {
    padding: 6px 10px;
    color: #245cc7;
    background: #eaf1ff;
    border-radius: 999px;
    font-size: 12px;
  }
}

.contract-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-bottom: 22px;
  padding: 12px 14px;
  color: #657084;
  background: #f7f9fc;
  border-radius: 8px;
  font-size: 13px;

  span:first-child {
    color: #202938;
    font-weight: 700;
  }
}

.section-lead {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  h3 {
    margin: 0 0 6px;
    color: #202938;
    font-size: 18px;
  }

  p {
    margin: 0;
    color: #727c8d;
    line-height: 1.6;
  }
}

.section-index {
  display: flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #fff;
  background: #3a7afe;
  border-radius: 50%;
  font-weight: 700;
}

.resume-hint,
.blocking-panel,
.seat-summary,
.manual-pending {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 8px;
  line-height: 1.6;
}

.resume-hint {
  color: #46627f;
  background: #f0f6ff;
}

.blocking-panel,
.seat-summary--over {
  color: #a33a22;
  background: #fff1ed;
  border: 1px solid #ffd2c7;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;

  &--center {
    justify-content: center;
  }
}

.challenge-list {
  display: grid;
  gap: 12px;
}

.challenge-card {
  padding: 14px;
  border: 1px solid #e3e7ee;
  border-radius: 10px;

  &__meta,
  &__action {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__meta {
    margin-bottom: 12px;
    color: #6b7483;

    strong {
      color: #273142;
    }

    .verified {
      margin-left: auto;
      color: #16845b;
    }

    .pending {
      margin-left: auto;
      color: #a36a12;
    }
  }

  &__action .el-input {
    flex: 1;
  }
}

.preflight-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 0 0 16px;

  div {
    padding: 12px;
    background: #f7f9fc;
    border-radius: 8px;
  }

  dt {
    margin-bottom: 5px;
    color: #8992a1;
    font-size: 12px;
  }

  dd {
    margin: 0;
    color: #273142;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
}

.destructive-panel {
  display: grid;
  gap: 6px;
  padding: 14px;
  color: #873418;
  background: #fff5e9;
  border: 1px solid #ffd7ad;
  border-radius: 8px;
}

.irreversible-checkbox {
  margin-top: 16px;
  white-space: normal;
}

.seat-summary {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 16px;
  color: #2c6d56;
  background: #edf9f4;
}

.member-regions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.member-region {
  min-width: 0;
  padding: 14px;
  border: 1px solid #e4e8ef;
  border-radius: 10px;

  h4 {
    margin: 0 0 12px;
    color: #273142;
  }
}

.member-list {
  display: grid;
  gap: 8px;
  max-height: 190px;
  overflow: auto;
}

.member-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 10px;
  background: #f8f9fb;
  border-radius: 7px;

  > div:first-child {
    display: grid;
    min-width: 0;

    strong,
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    span {
      color: #7a8494;
      font-size: 12px;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 5px 8px;
    color: #7a8494;
    font-size: 12px;
  }
}

.allocation-panel {
  margin-top: 18px;
  padding: 16px;
  background: #f7f9fc;
  border-radius: 10px;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    h4 {
      margin: 0 0 5px;
      color: #273142;
    }

    p {
      margin: 0;
      color: #727c8d;
    }
  }
}

.quota-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;

  .el-input-number {
    width: 100%;
  }

  small {
    display: block;
    margin-top: 5px;
    color: #7a8494;
  }
}

.waiting-section,
.success-section {
  padding: 28px 0 10px;
  text-align: center;

  h3 {
    margin: 14px 0 8px;
    color: #273142;
    font-size: 19px;
  }

  > p {
    color: #707b8c;
  }
}

.waiting-spinner {
  width: 44px;
  height: 44px;
  margin: 0 auto;
  border: 4px solid #dce8ff;
  border-top-color: #3a7afe;
  border-radius: 50%;
  animation: global-biz-spin 1s linear infinite;
}

.manual-pending {
  display: grid;
  gap: 5px;
  max-width: 560px;
  margin: 20px auto 0;
  color: #865311;
  background: #fff7df;
  border: 1px solid #ffe1a0;
  text-align: left;
}

.success-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  margin: 0 auto;
  color: #fff;
  background: #20a36a;
  border-radius: 50%;
  font-size: 28px;
}

@keyframes global-biz-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 720px) {
  .el-dialog.global-biz-opening {
    .el-dialog__header,
    .el-dialog__body {
      padding-right: 18px !important;
      padding-left: 18px !important;
    }
  }

  .opening-header,
  .seat-summary,
  .allocation-panel__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .preflight-summary,
  .member-regions,
  .quota-grid {
    grid-template-columns: 1fr;
  }

  .challenge-card__action,
  .member-row {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>

<style lang="scss">
.el-dialog.global-biz-opening {
  width: 520px !important;
  max-width: calc(100vw - 32px) !important;
  max-height: calc(100vh - 32px);
  padding: 28px !important;
  overflow-y: auto;
  border-radius: 14px !important;
  box-shadow: 0 16px 44px rgba(10, 35, 77, .2);

  .el-dialog__header {
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
  }

  .el-dialog__body {
    padding: 0 !important;
  }

  .el-input__wrapper,
  .el-input-number .el-input__wrapper {
    min-height: 38px;
    border-radius: 8px;
    box-shadow: 0 0 0 1px #d9dee5 inset;
  }

  .el-button {
    min-height: 38px;
    border-radius: 8px;
  }
}

.opening-body,
.opening-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: #1f2329;
  font-family: Inter, "PingFang SC", "Microsoft YaHei", sans-serif;
}

.verification-section {
  min-height: 548px;
}

.opening-success-head {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  text-align: center;

  &__icon.enterprise-svg-icon {
    width: 44px;
    height: 44px;
    color: #00b578;
  }

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: #1f2329;
    font-size: 19px;
    font-weight: 800;
    line-height: 1.35;
  }

  p {
    color: #8f959e;
    font-size: 12.5px;
    line-height: 1.5;
  }
}

.opening-divider {
  height: 1px;
  background: #eef1f5;
}

.opening-section-title {
  display: flex;
  align-items: center;
  gap: 8px;

  .enterprise-svg-icon {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
    color: #2b7de9;
  }

  h3 {
    margin: 0;
    color: #1f2329;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.45;
  }
}

.opening-title {
  margin: 0;
  color: #1f2329;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.4;
}

.opening-description,
.opening-footnote {
  margin: 0;
  color: #51565d;
  font-size: 12.5px;
  line-height: 1.55;
}

.opening-footnote {
  color: #8f959e;
  font-size: 11.5px;
  text-align: left;
}

.verification-field {
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    color: #51565d;
    font-size: 12.5px;
    font-weight: 600;
  }

  > .el-input .el-input__inner {
    color: #98a2b3;
    font-size: 13px;
  }
}

.verification-code-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 112px;
  gap: 10px;

  .el-input__inner {
    font-size: 13px;
  }

  .el-button {
    padding: 0 14px;
    color: #2b7de9 !important;
    font-size: 13.5px;
    font-weight: 600;
    border-color: #2b7de9 !important;
    background: #fff !important;
  }

  .el-button:hover:not(.is-disabled) {
    background: #f5f9ff !important;
  }
}

.opening-primary-button {
  width: 100%;
  min-height: 40px !important;
  margin: 0 !important;
  border-color: #2b7de9 !important;
  background: #2b7de9 !important;
  font-weight: 600;
}

.opening-primary-button.is-disabled,
.opening-primary-button.is-disabled:hover {
  color: #fff !important;
  border-color: #2b7de9 !important;
  background: #2b7de9 !important;
  opacity: 1;
}

.preflight-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;

  div {
    display: grid;
    grid-template-columns: 86px minmax(0, 1fr);
    gap: 10px;
    padding: 12px 14px;
    background: #f7f9fc;
    border-radius: 10px;
  }

  dt,
  dd {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
  }

  dt { color: #8f959e; }
  dd { color: #1f2329; font-weight: 600; overflow-wrap: anywhere; }
}

.binding-section .preflight-summary {
  gap: 0;
  overflow: hidden;
  background: #f7f9fc;
  border: 1px solid #eef1f5;
  border-radius: 10px;

  div {
    padding: 11px 14px;
    background: transparent;
    border-bottom: 1px solid #e8edf3;
    border-radius: 0;
  }

  div:last-child {
    border-bottom: 0;
  }
}

.destructive-panel,
.blocking-panel,
.manual-pending {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.55;
}

.destructive-panel {
  color: #873418;
  background: #fff5e9;
  border: 1px solid #ffd7ad;
}

.blocking-panel {
  color: #a33a22;
  background: #fff1ed;
  border: 1px solid #ffd2c7;
}

.irreversible-checkbox {
  margin: 0;
  white-space: normal;

  .el-checkbox__label {
    color: #51565d;
    line-height: 1.55;
    white-space: normal;
  }
}

.quota-allocation-bar {
  display: flex;
  gap: 4px;
  width: 100%;
  height: 10px;

  span { min-width: 0; border-radius: 5px; }
  &__vip { background: #2b7de9; }
  &__ai { background: #93c5fd; }
}

.quota-presets {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  button {
    min-height: 27px;
    padding: 0 14px;
    color: #51565d;
    background: #fff;
    border: 1px solid #d9dee5;
    border-radius: 999px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
  }

  button.active {
    color: #2b7de9;
    background: #eaf3ff;
    border-color: #2b7de9;
    font-weight: 600;
  }
}

.quota-region-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.quota-region-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 14px;
  background: #f7f9fc;
  border-radius: 10px;

  strong {
    color: #51565d;
    font-size: 12.5px;
    font-weight: 700;
  }

  &__input {
    display: flex;
    align-items: flex-end;
    gap: 8px;

    .el-input-number { width: 100%; min-width: 0; }
    span { flex: none; color: #8f959e; font-size: 12px; }
  }
}

.allocation-note,
.member-cleanup-panel__warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  color: #386a9f;
  background: #eaf3ff;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;

  .el-icon { flex: none; margin-top: 2px; }
}

.allocation-note {
  padding: 0;
  color: #8f959e;
  background: transparent;
  border-radius: 0;

  .enterprise-svg-icon {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    margin-top: 2px;
  }
}

.allocation-reminder-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    color: #8f959e;
    background: transparent;
    border: 0;
    cursor: pointer;
  }

  .enterprise-svg-icon {
    width: 18px;
    height: 18px;
  }
}

.allocation-reminder-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  color: #92400e;
  background: #fff7e6;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.55;

  .enterprise-svg-icon {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    color: #d97706;
  }
}

.allocation-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  .el-button + .el-button {
    margin-left: 0;
  }

  .opening-primary-button {
    font-size: 13.5px;
  }
}

.allocation-secondary-button {
  width: 100%;
  min-height: 40px !important;
  margin: 0 !important;
  color: #51565d !important;
  border-color: #d9dee5 !important;
  background: #fff !important;
  font-size: 13.5px;
  font-weight: 600;
}

.allocation-footnote {
  font-size: 11.5px;
}

.allocation-section {
  min-height: 0;
}

.allocation-section--reminder {
  min-height: 0;
}

.allocation-reminder-submit {
  font-size: 13.5px;
}

.allocation-section > .opening-description {
  font-size: 13px;
}

.member-cleanup-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.member-cleanup-panel__warning {
  color: #873418;
  background: #fff5e9;
}

.member-cleanup-region {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 12px;
  border: 1px solid #eef1f5;
  border-radius: 10px;

  > strong { font-size: 13px; }
}

.member-cleanup-row,
.allocation-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  padding: 10px 12px;
  background: #f7f9fc;
  border-radius: 8px;
  font-size: 13px;

  span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}

.waiting-section,
.success-section {
  padding: 0;
  text-align: center;
}

.success-section {
  .opening-success-head {
    gap: 10px;
  }

  .allocation-summary-row {
    padding: 12px 14px;
    border-radius: 10px;
    text-align: left;

    strong {
      color: #51565d;
      font-weight: 700;
    }

    span {
      color: #00b578;
      font-weight: 700;
    }
  }

  > .opening-description {
    text-align: left;
  }

  > .opening-primary-button {
    font-size: 13.5px;
  }
}

.waiting-spinner {
  width: 44px;
  height: 44px;
  margin: 0 auto;
  border: 4px solid #dce8ff;
  border-top-color: #2b7de9;
  border-radius: 50%;
  animation: global-biz-spin 1s linear infinite;
}

.waiting-section h3 {
  margin: 0;
  color: #1f2329;
  font-size: 18px;
}

.waiting-section > p {
  margin: 0;
  color: #51565d;
  font-size: 13px;
  line-height: 1.6;
}

.manual-pending {
  color: #865311;
  background: #fff7df;
  border: 1px solid #ffe1a0;
  text-align: left;
}

@media (max-width: 560px) {
  .el-dialog.global-biz-opening {
    padding: 18px !important;
  }

  .quota-region-grid {
    grid-template-columns: 1fr;
  }
}
</style>
